import { useState, useEffect, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X, ArrowUp, AlertCircle, Loader2, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Review } from "@/types/review";
import { Session } from "@supabase/supabase-js";
import { cn } from "@/lib/utils";

interface CommentInputBarProps {
  bookId: number;
  replyTo: Review | null;
  session: Session | null;
  hasCompletedBook: boolean;
  isCheckingCompletion: boolean;
  onSubmit: (params: { content: string; isAnonymous: boolean; rating: number; replyToId?: string }) => Promise<void>;
  onCancelReply: () => void;
  onAuthRequired: () => void;
}

export function CommentInputBar({
  replyTo,
  session,
  hasCompletedBook,
  onSubmit,
  onCancelReply,
  onAuthRequired,
}: CommentInputBarProps) {
  const [content, setContent] = useState("");
  const [rating, setRating] = useState(5);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isContentModerated, setIsContentModerated] = useState(false);
  const [isCheckingContent, setIsCheckingContent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const maxChars = 500;
  const charCount = content.length;
  const isOverLimit = charCount > maxChars;
  const canSubmit = content.trim().length > 0 && !isOverLimit && !isContentModerated && !isCheckingContent && !isSubmitting;

  // Auto-focus when entering reply mode
  useEffect(() => {
    if (replyTo && textareaRef.current) {
      const id = setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [replyTo]);

  // Reset textarea height when content is cleared
  useEffect(() => {
    if (content === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [content]);

  // Debounced AI content moderation
  useEffect(() => {
    if (!content.trim()) {
      setIsContentModerated(false);
      setIsCheckingContent(false);
      return;
    }

    setIsCheckingContent(true);
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('moderate-content', {
          body: { content },
        });

        if (error) {
          setIsContentModerated(false);
        } else {
          setIsContentModerated(data?.flagged === true);
        }
      } catch (error) {
        setIsContentModerated(false);
      } finally {
        setIsCheckingContent(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [content]);

  const handleSubmit = async () => {
    console.log('🔵 CommentInputBar handleSubmit - replyTo:', replyTo);
    
    if (!session) {
      onAuthRequired();
      return;
    }

    if (!canSubmit) {
      console.log('🔴 Cannot submit - canSubmit is false');
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('🟢 Calling onSubmit with:', {
        content,
        isAnonymous,
        rating,
        replyToId: replyTo?.id,
      });
      
      await onSubmit({
        content,
        isAnonymous,
        rating,
        replyToId: replyTo?.id,
      });
      
      // Reset form
      setContent("");
      setRating(5);
      setIsAnonymous(false);
      console.log('✅ Form reset after successful submission');
    } catch (error) {
      console.error('🔴 Error in handleSubmit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = () => {
    if (!session) {
      onAuthRequired();
      textareaRef.current?.blur();
    }
  };

  const placeholder = !session 
    ? "Sign in to comment" 
    : replyTo 
    ? `Replying to ${replyTo.user_display_name || 'user'}...`
    : "Add a comment...";

  return (
    <div
      className="p-3 space-y-3"
      data-vaul-no-drag
      data-vaul-ignore-outside-clicks
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Reply Header */}
      {replyTo && (
        <div className="flex items-center justify-between pb-2">
          <span className="text-xs text-white/70">
            Replying to <span className="font-medium text-white">{replyTo.user_display_name || "user"}</span>
          </span>
          <button
            onClick={onCancelReply}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        </div>
      )}

      {/* Star Rating, Anonymous Toggle, and Character Count - Combined Section */}
      {!replyTo && session && (
        <div className="flex items-center justify-between px-1">
          {/* Anonymous Toggle */}
          <div className="flex flex-col items-center gap-1 w-20">
            <Switch
              id="anonymous-comment"
              checked={isAnonymous}
              onCheckedChange={setIsAnonymous}
              className="data-[state=checked]:bg-primary scale-75"
            />
            <Label htmlFor="anonymous-comment" className="text-white/60 cursor-pointer text-xs">
              Anonymous
            </Label>
          </div>

          {/* Star Rating Selector */}
          <div className="flex items-center justify-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className="p-0.5 transition-transform hover:scale-110 active:scale-95"
                data-vaul-no-drag
                data-vaul-ignore-outside-clicks
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <Star
                  className={cn(
                    "w-5 h-5 transition-all duration-200",
                    star <= rating
                      ? "fill-purple-400 text-purple-400 drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]"
                      : "fill-transparent text-white/30"
                  )}
                />
              </button>
            ))}
          </div>

          {/* Character Counter */}
          <div className="w-20 flex justify-end">
            <span className={cn("text-xs", isOverLimit ? "text-red-400" : "text-white/50")}>
              {charCount}/{maxChars}
            </span>
          </div>
        </div>
      )}

      {/* Input Container - Instagram Style */}
      <div className="flex items-center gap-2">
        {/* Textarea Wrapper */}
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              // Auto-resize textarea
              if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
              }
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder={placeholder}
            disabled={!session}
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); textareaRef.current?.focus(); }}
            onMouseDown={(e) => { e.stopPropagation(); }}
            onTouchStart={(e) => { e.stopPropagation(); }}
            onTouchEnd={(e) => { e.stopPropagation(); }}
            rows={1}
            className="relative z-10 min-h-[44px] max-h-[200px] bg-white/5 border border-white/10 text-white placeholder:text-white/40 resize-none pointer-events-auto rounded-3xl px-4 py-3 pr-14 overflow-y-auto outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent focus:ring-transparent focus-visible:ring-offset-transparent focus:border-white/10 transition-none"
            style={{ pointerEvents: 'auto', WebkitAppearance: 'none', outline: 'none', boxShadow: 'none', WebkitTapHighlightColor: 'transparent' }}
            data-vaul-no-drag
            data-vaul-ignore-outside-clicks
            autoFocus={!!replyTo}
          />
          
          {/* Arrow Button Inside Input - Higher z-index */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || !session}
            className={cn(
              "absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all z-50",
              canSubmit && session
                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                : "bg-white/10 text-white/40 cursor-not-allowed"
            )}
            data-vaul-no-drag
            data-vaul-ignore-outside-clicks
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {isSubmitting || isCheckingContent ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
            )}
          </button>
        </div>
      </div>

      {/* Moderation Status */}
      {(isCheckingContent || isContentModerated) && (
        <div className="flex items-center gap-3 px-1">
          {isCheckingContent && (
            <div className="flex items-center gap-1 text-white/50 text-xs">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Checking...</span>
            </div>
          )}
          {isContentModerated && (
            <div className="flex items-center gap-1 text-red-400 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>Not permitted</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
